import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Box,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React, { useState, useEffect, useMemo } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

export interface PrescriptionFilterValues {
  statuses: string[];
  fromDate: Date | null;
  toDate: Date | null;
  fromPrice: string;
  toPrice: string;
  providerIds: string[];
}

export interface ProviderOption {
  providerId: string;
  providerName: string;
  providerEmail: string;
}

interface PrescriptionFilterProps {
  providerOptions: ProviderOption[];
  onApply: (filters: PrescriptionFilterValues) => void;
  initialFilters?: PrescriptionFilterValues;
}

const PrescriptionFilter: React.FC<PrescriptionFilterProps> = ({
  providerOptions,
  onApply,
  initialFilters,
}) => {
  const { colors } = useCelleraTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    initialFilters?.statuses || []
  );
  const [fromDate, setFromDate] = useState<Date | null>(
    initialFilters?.fromDate || null
  );
  const [toDate, setToDate] = useState<Date | null>(
    initialFilters?.toDate || null
  );
  const [fromPrice, setFromPrice] = useState<string>(
    initialFilters?.fromPrice || ""
  );
  const [toPrice, setToPrice] = useState<string>(initialFilters?.toPrice || "");
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>(
    initialFilters?.providerIds || []
  );

  const open = Boolean(anchorEl);

  // Reset local state when initialFilters change
  useEffect(() => {
    if (initialFilters) {
      setSelectedStatuses(initialFilters.statuses || []);
      setFromDate(initialFilters.fromDate || null);
      setToDate(initialFilters.toDate || null);
      setFromPrice(initialFilters.fromPrice || "");
      setToPrice(initialFilters.toPrice || "");
      setSelectedProviderIds(initialFilters.providerIds || []);
    }
  }, [initialFilters]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProviderChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedProviderIds(event.target.value as string[]);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedStatuses.length > 0) count++;
    if (fromDate) count++;
    if (toDate) count++;
    if (fromPrice) count++;
    if (toPrice) count++;
    if (selectedProviderIds.length > 0) count++;
    return count;
  }, [
    selectedStatuses,
    fromDate,
    toDate,
    fromPrice,
    toPrice,
    selectedProviderIds,
  ]);

  const handleApply = (clear: boolean) => {
    if (clear) {
      const emptyFilters: PrescriptionFilterValues = {
        statuses: [],
        fromDate: null,
        toDate: null,
        fromPrice: "",
        toPrice: "",
        providerIds: [],
      };
      setSelectedStatuses([]);
      setFromDate(null);
      setToDate(null);
      setFromPrice("");
      setToPrice("");
      setSelectedProviderIds([]);
      onApply(emptyFilters);
    } else {
      onApply({
        statuses: selectedStatuses,
        fromDate,
        toDate,
        fromPrice,
        toPrice,
        providerIds: selectedProviderIds,
      });
    }
    setAnchorEl(null);
  };

  return (
    <Box className="p-0 m-0">
      <Button
        id="prescription-filter-button"
        sx={{
          "&:hover": {
            borderColor: colors.background.darkGreenHighlight,
            "& #prescription-filter-count": {
              backgroundColor: colors.background.darkGreenHighlight,
            },
          },
          color: colors.text.primary,
          backgroundColor: colors.background.paper,
          transition: "background-color 0.2s ease",
        }}
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={handleClick}
      >
        Filter
        <Box
          id="prescription-filter-count"
          className="rounded-full flex items-center justify-center ml-2"
          sx={{
            backgroundColor: colors.primary.main,
            color: colors.text.inverse,
            transition: "background-color 0.2s ease",
            width: "23px",
            height: "23px",
          }}
        >
          {activeFilterCount}
        </Box>
      </Button>
      <Menu
        id="prescription-filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "3px",
            p: 0,
            m: 0,
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: 500,
              maxHeight: 800,
              overflowY: "auto",
              p: 2,
            }}
          >
            <Typography variant="h5">Filter</Typography>

            <Divider />

            {/* Date Range Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Date Range
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  maxDate={toDate || undefined}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  minDate={fromDate || undefined}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Price Range Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Price Range
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="From Price"
                  type="number"
                  size="small"
                  value={fromPrice}
                  onChange={(e) => setFromPrice(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  fullWidth
                />
                <TextField
                  label="To Price"
                  type="number"
                  size="small"
                  value={toPrice}
                  onChange={(e) => setToPrice(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  fullWidth
                />
              </Box>
            </Box>

            <Divider />

            {/* Provider Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Providers
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Select Staff</InputLabel>
                <Select
                  multiple
                  value={selectedProviderIds}
                  onChange={(event) =>
                    handleProviderChange(
                      event as React.ChangeEvent<{ value: unknown }>
                    )
                  }
                  input={<OutlinedInput label="Select Staff" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const provider = providerOptions.find(
                          (p) => p.providerId === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={provider?.providerName || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {providerOptions.map((provider) => (
                    <MenuItem
                      key={provider.providerId}
                      value={provider.providerId}
                    >
                      <Checkbox
                        checked={selectedProviderIds.includes(
                          provider.providerId
                        )}
                      />
                      <ListItemText
                        primary={provider.providerName}
                        secondary={provider.providerEmail}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </LocalizationProvider>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, p: 1 }}>
          <Button
            onClick={() => handleApply(true)}
            variant="outlined"
            color="primary"
            fullWidth
            sx={{
              width: "30%",
              borderRadius: "80px",
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={() => handleApply(false)}
            className="rounded-full"
            variant="outlined"
            fullWidth
            disabled={activeFilterCount === 0}
            sx={{
              borderRadius: "80px",
              width: "30%",
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default PrescriptionFilter;
